import { capitalize, concat, get, isFunction, map } from 'lodash';
import * as XLSX from 'xlsx';

/**
 * Write table rows to an xlsx file the browser downloads.
 *
 * MOVED OUT OF `TableMixin` VERBATIM, which is why it still reads like a mixin method. It never used
 * `this`, so it was a free function living in an Options API mixin — and a page written with
 * `<script setup>` cannot mix one in. Rewriting it at the same time would have changed behaviour for
 * every table already exporting through it: `get(row, column.field)` resolves dotted paths and
 * tolerates a column whose `field` is a function, and neither is obvious from the call sites.
 *
 * A COLUMN CAN SAY HOW IT EXPORTS. `exportFormatExcel` wins over `format`, so a column can render
 * one thing on screen and write another to the file — a date shown short and exported in full, for
 * instance. Without either, the raw value is written.
 *
 * @param {Object[]} data - the rows, already filtered and ordered as the file should be
 * @param {Object[]} columns - Quasar-shaped column definitions
 * @param {string} filename - including its extension
 */
// eslint-disable-next-line import/prefer-default-export
export async function exportTableDataToSpreadsheet(data, columns, filename) {
  const workbook = XLSX.utils.book_new();

  const exportColumns = map(
    columns,
    (column) => (column.label || column.exportLabel || ''),
  );

  const exportData = map(
    data,
    (row) => map(
      columns,
      (column) => {
        const field = typeof column.field === 'function' ? column.field(row) : get(row, column.field, '');
        if (isFunction(column.exportFormatExcel)) {
          return column.exportFormatExcel(field, row);
        }
        if (isFunction(column.format)) {
          return column.format(field, row);
        }
        return field;
      },
    ),
  );

  /* Create worksheet */
  const wsData = concat([exportColumns], exportData);
  const worksheet = XLSX.utils.aoa_to_sheet(wsData, { cellDates: true });
  const workbookName = capitalize(filename.split('.')[0]);

  /* Add the worksheet to the workbook */
  XLSX.utils.book_append_sheet(workbook, worksheet, workbookName);
  await XLSX.writeFile(workbook, filename);
} // exportTableDataToSpreadsheet()
